import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/api/firebase/firebase";
import {
  Cohort,
  CreateCohortRequest,
  UpdateCohortRequest,
} from "@/types/cohort";
import { getFirestore } from "firebase/firestore";

export class CohortService {
  private static readonly COLLECTION_NAME = "cohorts";

  static async createCohort(
    cohortData: CreateCohortRequest,
    currentUserId: string
  ): Promise<string> {
    try {
      const newCohort = {
        ...cohortData,
        status: "upcoming",
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: currentUserId,
        transactionViewMode: "default",
      };

      const docRef = await addDoc(
        collection(db, this.COLLECTION_NAME),
        newCohort
      );

      return docRef.id;
    } catch (error) {
      console.error("Error creating cohort:", error);
      throw new Error("Failed to create cohort");
    }
  }

  static async getAllCohorts(): Promise<Cohort[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      const cohorts: Cohort[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        cohorts.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Cohort);
      });

      return cohorts;
    } catch (error) {
      console.error("Error fetching cohorts:", error);
      throw new Error("Failed to fetch cohorts");
    }
  }

  static async getCohortsByStatus(status: string): Promise<Cohort[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where("status", "==", status),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      const cohorts: Cohort[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        cohorts.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Cohort);
      });

      return cohorts;
    } catch (error) {
      console.error(`Error fetching ${status} cohorts:`, error);
      throw new Error(`Failed to fetch ${status} cohorts`);
    }
  }

  static async getCohortById(id: string): Promise<Cohort | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const cohort = {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Cohort;

        return cohort;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error fetching cohort:", error);
      throw new Error("Failed to fetch cohort");
    }
  }

  static async updateCohort(
    id: string,
    updates: UpdateCohortRequest
  ): Promise<void> {
    try {
      // Get the current document data first
      const docRef = doc(db, this.COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Cohort document not found");
      }

      const currentData = docSnap.data();

      // The custom set is only removed when the update explicitly disables
      // custom transactions. Callers that don't mention it (status fixes,
      // payment edits, pending actions, saving one tab of transactions) must
      // keep the stored custom set — this whole document is replaced below, so
      // leaving it out deleted the cohort's custom transactions.
      const keepCustom =
        updates.enableCustomTransactions === true ||
        (updates.enableCustomTransactions !== false &&
          (!!updates.transactionConfig?.custom ||
            !!currentData.transactionConfig?.custom));

      // Prepare the update data with the new transaction config structure
      const updateData = {
        ...currentData,
        ...updates,
        updatedAt: Timestamp.now(),
        transactionConfig: {
          default: {
            groupId:
              updates.transactionConfig?.default?.groupId ??
              currentData.transactionConfig?.default?.groupId ??
              "",
            transactions: Array.isArray(
              updates.transactionConfig?.default?.transactions
            )
              ? updates.transactionConfig.default.transactions
              : Array.isArray(
                  currentData.transactionConfig?.default?.transactions
                )
              ? currentData.transactionConfig.default.transactions
              : [],
          },
          ...(keepCustom && {
            custom: {
              groupId:
                updates.transactionConfig?.custom?.groupId ??
                currentData.transactionConfig?.custom?.groupId ??
                "",
              transactions: Array.isArray(
                updates.transactionConfig?.custom?.transactions
              )
                ? updates.transactionConfig.custom.transactions
                : Array.isArray(
                    currentData.transactionConfig?.custom?.transactions
                  )
                ? currentData.transactionConfig.custom.transactions
                : [],
            },
          }),
        },
      };

      // Use setDoc without merge option since we're providing the complete document
      const { setDoc } = await import("firebase/firestore");
      await setDoc(docRef, updateData);

      // Verify the update
      await this.verifyCohortFields(id);
    } catch (error) {
      console.error("Error updating cohort:", error);
      throw error;
    }
  }

  /**
   * Verifies that the cohort has the expected fields in Firestore
   */
  static async verifyCohortFields(id: string): Promise<void> {
    try {
      const db = getFirestore();
      const docRef = doc(db, this.COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return;
      }

      const data = docSnap.data();
      const config = data.transactionConfig;
    } catch (error) {
      console.error("Error verifying cohort fields:", error);
    }
  }

  static async deleteCohort(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting cohort:", error);
      throw new Error("Failed to delete cohort");
    }
  }

  static async getCohortsByTrainer(trainerId: string): Promise<Cohort[]> {
    try {
      // Fetch all cohorts and filter client-side to avoid composite index requirement
      const q = query(collection(db, this.COLLECTION_NAME));
      const querySnapshot = await getDocs(q);
      const cohorts: Cohort[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.trainerAssignment?.trainerId === trainerId) {
          cohorts.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Cohort);
        }
      });

      // Sort by createdAt descending
      cohorts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      return cohorts;
    } catch (error) {
      console.error("Error fetching trainer cohorts:", error);
      throw new Error("Failed to fetch trainer cohorts");
    }
  }

  static async getCohortsByClient(clientId: string): Promise<Cohort[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where("selectedClients", "array-contains-any", [{ clientId }]),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      const cohorts: Cohort[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Additional filtering since Firestore array-contains-any isn't perfect for object arrays
        const hasClient = data.selectedClients?.some(
          (client: any) => client.clientId === clientId
        );
        if (hasClient) {
          cohorts.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Cohort);
        }
      });

      return cohorts;
    } catch (error) {
      console.error("Error fetching client cohorts:", error);
      throw new Error("Failed to fetch client cohorts");
    }
  }

  static async getCohortByParticipantEmail(
    email: string
  ): Promise<Cohort | null> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);

      for (const doc of querySnapshot.docs) {
        const data = doc.data();

        // Check if the cohort has participants
        if (!data.participants || data.participants.length === 0) {
          continue;
        }

        // Log participants emails for debugging
        const participantEmails = data.participants.map((p: any) => p.email);

        // Check if the email matches any participant in this cohort
        const isParticipant = data.participants?.some(
          (participant: any) =>
            participant.email?.toLowerCase() === email.toLowerCase()
        );

        if (isParticipant) {
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Cohort;
        }
      }

      return null;
    } catch (error) {
      console.error(
        "CohortService - Error fetching cohort by participant email:",
        error
      );
      throw new Error("Failed to fetch cohort by participant email");
    }
  }

  /**
   * Set the transaction view mode for a cohort
   * @param cohortId The ID of the cohort
   * @param viewMode The transaction view mode ("default" or "custom")
   * @returns Promise that resolves when the update is complete
   */
  static async setTransactionViewMode(
    cohortId: string,
    viewMode: "default" | "custom"
  ): Promise<void> {
    try {
      if (!cohortId) {
        throw new Error("Cohort ID is required");
      }

      if (viewMode !== "default" && viewMode !== "custom") {
        throw new Error(`Invalid view mode: ${viewMode}`);
      }

      const db = getFirestore();
      const cohortRef = doc(db, this.COLLECTION_NAME, cohortId);

      // Verify the cohort exists first
      const cohortDoc = await getDoc(cohortRef);
      if (!cohortDoc.exists()) {
        throw new Error(`Cohort ${cohortId} not found`);
      }

      const cohortData = cohortDoc.data();

      // Update the transaction view mode
      await updateDoc(cohortRef, {
        transactionViewMode: viewMode,
        updatedAt: Timestamp.now(),
      });

      // Verify the update was successful
      const updatedDoc = await getDoc(cohortRef);
      const updatedData = updatedDoc.data();

      if (updatedData?.transactionViewMode !== viewMode) {
        console.error(
          `CohortService - View mode verification failed! Expected ${viewMode}, got ${updatedData?.transactionViewMode}`
        );
        throw new Error("View mode update failed verification");
      }
    } catch (error) {
      console.error(
        "CohortService - Error setting transaction view mode:",
        error
      );
      throw error;
    }
  }

  /**
   * Get the transaction view mode for a cohort
   * @param cohortId The ID of the cohort
   * @returns Promise that resolves to the transaction view mode
   */
  static async getTransactionViewMode(
    cohortId: string
  ): Promise<"default" | "custom"> {
    try {
      if (!cohortId) {
        console.warn(
          "CohortService - No cohort ID provided when getting view mode"
        );
        return "default";
      }

      // Get a fresh copy of the cohort document directly from Firestore
      const db = getFirestore();
      const cohortRef = doc(db, this.COLLECTION_NAME, cohortId);
      const cohortDoc = await getDoc(cohortRef);

      if (!cohortDoc.exists()) {
        console.warn(
          "CohortService - Cohort not found, defaulting to 'default' view mode"
        );
        return "default";
      }

      const cohortData = cohortDoc.data();

      // If transactionViewMode is explicitly set, use it
      if (
        cohortData?.transactionViewMode === "default" ||
        cohortData?.transactionViewMode === "custom"
      ) {
        return cohortData.transactionViewMode;
      }

      // If no explicit view mode, check if custom transactions are enabled
      if (
        cohortData?.enableCustomTransactions &&
        cohortData?.transactionConfig?.custom?.transactions?.length > 0
      ) {
        // Update the document to set the explicit mode
        await updateDoc(cohortRef, {
          transactionViewMode: "custom",
          updatedAt: Timestamp.now(),
        });
        return "custom";
      }

      // Default case: set and return 'default' mode

      await updateDoc(cohortRef, {
        transactionViewMode: "default",
        updatedAt: Timestamp.now(),
      });
      return "default";
    } catch (error) {
      console.error(
        "CohortService - Error getting transaction view mode:",
        error
      );
      return "default";
    }
  }

  /**
   * Force refresh the transaction view mode for a cohort directly from the database
   * This method bypasses any caching and always fetches fresh data
   * @param cohortId The ID of the cohort
   * @returns Promise that resolves to the transaction view mode
   */
  static async forceRefreshTransactionViewMode(
    cohortId: string
  ): Promise<"default" | "custom"> {
    try {
      if (!cohortId) {
        return "default";
      }

      // Create a new Firestore instance to bypass any caching
      const freshDb = getFirestore();
      const cohortRef = doc(freshDb, "cohorts", cohortId);

      // Force a server read with no cache
      const cohortDoc = await getDoc(cohortRef);

      if (!cohortDoc.exists()) {
        console.warn(
          `CohortService - Cohort ${cohortId} not found during force refresh`
        );
        return "default";
      }

      const cohortData = cohortDoc.data();
      const viewMode = cohortData?.transactionViewMode || "default";

      // Validate and log the result
      if (viewMode !== "default" && viewMode !== "custom") {
        console.warn(
          `CohortService - Invalid view mode found during force refresh: ${viewMode}`
        );
        return "default";
      }

      return viewMode;
    } catch (error) {
      console.error(
        "CohortService - Error during force refresh of view mode:",
        error
      );
      return "default";
    }
  }

  /**
   * Debug method to check a cohort's transaction view mode directly from the database
   * Use this for troubleshooting when the view mode isn't working properly
   * @param cohortId The ID of the cohort
   */
  static async debugCohortViewMode(cohortId: string): Promise<void> {
    try {
      if (!cohortId) {
        console.error("CohortService DEBUG - No cohort ID provided");
        return;
      }

      // Get a fresh copy of the cohort document directly from Firestore
      const db = getFirestore();
      const cohortRef = doc(db, "cohorts", cohortId);
      const cohortDoc = await getDoc(cohortRef);

      if (!cohortDoc.exists()) {
        console.error(`CohortService DEBUG - Cohort ${cohortId} not found!`);
        return;
      }

      const cohortData = cohortDoc.data();

      // IMPORTANT: Focus on the transactions stored directly in the cohort document
      if (cohortData?.transactions && cohortData.transactions.length > 0) {
        // Analyze transaction flags
        const statsStatement = cohortData.transactions.filter(
          (t: any) => t.includeStatement
        ).length;
        const statsCurrent = cohortData.transactions.filter(
          (t: any) => t.includeCurrent
        ).length;
        const statsDirect = cohortData.transactions.filter(
          (t: any) => t.includeDirect
        ).length;
      } else {
        console.warn(
          "CohortService DEBUG - No transactions found in cohort document!"
        );
      }

      // Check if view mode is correctly set
      if (!cohortData?.transactionViewMode) {
      } else if (
        cohortData.transactionViewMode !== "default" &&
        cohortData.transactionViewMode !== "custom"
      ) {
        console.error(
          `CohortService DEBUG - Invalid transaction view mode: ${cohortData.transactionViewMode}`
        );
      } else {
      }

      // Suggest fixes if applicable
      if (
        cohortData?.transactionViewMode === "custom" &&
        !cohortData?.enableCustomTransactions
      ) {
        console.warn(
          "CohortService DEBUG - Custom view mode is set but enableCustomTransactions is false!"
        );
      }
    } catch (error) {
      console.error("CohortService DEBUG - Error checking cohort:", error);
    }
  }

  /**
   * Delete a cohort and all of its associated data including participants
   * @param cohortId The ID of the cohort to delete
   * @returns Array of participant emails that need to be manually deleted from Firebase Auth
   */
  static async getTrainerIdByEmail(email: string): Promise<string | null> {
    try {
      const normalized = email.trim().toLowerCase();
      const trainersQuery = query(
        collection(db, "trainers"),
        where("email", "==", normalized)
      );
      const snapshot = await getDocs(trainersQuery);

      if (!snapshot.empty) {
        return snapshot.docs[0].id;
      }

      // Fallback for legacy trainer docs saved with mixed-case/whitespace emails
      // (Firebase Auth emails are always lowercase, so an exact match misses them)
      const allTrainers = await getDocs(collection(db, "trainers"));
      const match = allTrainers.docs.find(
        (d) => (d.data().email || "").trim().toLowerCase() === normalized
      );
      return match ? match.id : null;
    } catch (error) {
      console.error("Error finding trainer by email:", error);
      return null;
    }
  }

  static async deleteEntireCohort(cohortId: string): Promise<string[]> {
    try {
      if (!cohortId) {
        throw new Error("Cohort ID is required");
      }

      // 1. Get the cohort data first to get the list of participants
      const cohortRef = doc(db, this.COLLECTION_NAME, cohortId);
      const cohortDoc = await getDoc(cohortRef);

      if (!cohortDoc.exists()) {
        throw new Error(`Cohort ${cohortId} not found`);
      }

      const cohortData = cohortDoc.data();
      const participants = cohortData.participants || [];
      const participantEmails: string[] = participants.map((p: any) => p.email);

      const progressQuery = query(
        collection(db, "userProgress"),
        where("cohortId", "==", cohortId)
      );

      const progressSnapshot = await getDocs(progressQuery);
      const progressDeletions = progressSnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(progressDeletions);

      // 3. Find and handle participant user documents
      const { arrayRemove } = await import("firebase/firestore");
      const userDeletions: Promise<void>[] = [];
      const participantOnlyEmails: string[] = [];

      for (const email of participantEmails) {
        // Find the user document with this email
        const usersQuery = query(
          collection(db, "users"),
          where("email", "==", email)
        );

        const userSnapshot = await getDocs(usersQuery);

        if (!userSnapshot.empty) {
          const userDoc = userSnapshot.docs[0];
          const userData = userDoc.data();

          if (userData.role === "trainer" || userData.role === "admin") {
            // Don't delete trainer/admin accounts - just remove this cohort
            userDeletions.push(
              updateDoc(doc(db, "users", userDoc.id), {
                cohorts: arrayRemove(cohortId),
                updatedAt: Timestamp.now(),
              })
            );
          } else {
            // Safe to delete participant-only accounts
            userDeletions.push(deleteDoc(doc(db, "users", userDoc.id)));
            participantOnlyEmails.push(email);
          }
        } else {
          console.warn(`CohortService - User document not found for: ${email}`);
        }
      }

      await Promise.all(userDeletions);

      // 4. Delete the cohort document itself

      await deleteDoc(cohortRef);

      // 5. Display instructions for Firebase Auth deletion (only for participant-only accounts)
      if (participantOnlyEmails.length > 0) {
        console.warn(
          "IMPORTANT: Please delete these users from Firebase Authentication console manually:",
          participantOnlyEmails.join(", ")
        );

        console.info(
          "Steps to delete from Firebase Auth:\n",
          "1. Go to Firebase Console\n",
          "2. Select Authentication\n",
          "3. Find and delete each user with these emails\n"
        );
      }

      return participantOnlyEmails;
    } catch (error) {
      console.error("CohortService - Error deleting entire cohort:", error);
      throw error;
    }
  }
}
